import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import StatusBadge from '../StatusBadge';
import type { DuplicateCandidate } from '../../services/api';

/** Un fichier ré-uploadé en conflit avec un document existant (PROD1.10 #671). */
export interface UploadConflict {
  file: File;
  candidate: DuplicateCandidate;
}

/** Décision prise pour un conflit donné. 'ignore' par défaut. */
export type ConflictDecision = 'ignore' | 'reextract';

interface Props {
  open: boolean;
  conflicts: UploadConflict[];
  submitting?: boolean;
  /** Reçoit la décision par conflit (même ordre que `conflicts`). */
  onConfirm: (decisions: ConflictDecision[]) => void;
  onCancel: () => void;
}

/**
 * Modale récapitulative de ré-upload (volet B, #671).
 *
 * Quand un lot d'uploads contient ≥1 PDF déjà présent (même contenu), on
 * regroupe tous les conflits ici. Pour chaque ligne : « Ignorer » (défaut) ou
 * « Ré-extraire l'existant ». Pas d'option « créer un doublon » : jamais de 2e
 * document de contenu identique.
 */
const ReuploadDedupModal: React.FC<Props> = ({
  open,
  conflicts,
  submitting = false,
  onConfirm,
  onCancel,
}) => {
  const [decisions, setDecisions] = useState<ConflictDecision[]>([]);

  // Réinitialise les décisions (tout « Ignorer ») à chaque ouverture / nouveau lot.
  useEffect(() => {
    if (open) {
      setDecisions(conflicts.map(() => 'ignore'));
    }
  }, [open, conflicts]);

  const setDecision = (index: number, value: ConflictDecision) => {
    setDecisions((prev) => prev.map((d, i) => (i === index ? value : d)));
  };

  const setAll = (value: ConflictDecision) => {
    setDecisions(conflicts.map(() => value));
  };

  const reextractCount = decisions.filter((d) => d === 'reextract').length;

  return (
    <Modal isOpen={open} onClose={onCancel} title="Documents déjà présents" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {conflicts.length === 1
            ? 'Un fichier déposé a le même contenu qu’un document existant.'
            : `${conflicts.length} fichiers déposés ont le même contenu que des documents existants.`}{' '}
          Aucun doublon ne sera créé. Choisissez, pour chaque fichier, de l’ignorer
          ou de relancer une ré-extraction (nouvelle version) sur le document existant.
        </p>

        {/* Actions groupées */}
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-500">Tout :</span>
          <button
            type="button"
            onClick={() => setAll('ignore')}
            disabled={submitting}
            className="px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Ignorer tout
          </button>
          <button
            type="button"
            onClick={() => setAll('reextract')}
            disabled={submitting}
            className="px-3 py-1 rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          >
            Tout ré-extraire
          </button>
        </div>

        {/* Liste des conflits */}
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
          {conflicts.map((c, index) => (
            <li key={`${c.candidate.id}-${index}`} className="p-3 flex items-center gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 truncate" title={c.file.name}>
                  {c.file.name}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                  <span className="truncate" title={c.candidate.original_filename}>
                    Existant : {c.candidate.original_filename}
                  </span>
                  <StatusBadge status={c.candidate.status} />
                  {c.candidate.version != null && (
                    <span className="text-gray-400">v{c.candidate.version}</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4 shrink-0">
                <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name={`decision-${index}`}
                    checked={decisions[index] === 'ignore'}
                    onChange={() => setDecision(index, 'ignore')}
                    disabled={submitting}
                  />
                  Ignorer
                </label>
                <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name={`decision-${index}`}
                    checked={decisions[index] === 'reextract'}
                    onChange={() => setDecision(index, 'reextract')}
                    disabled={submitting}
                  />
                  Ré-extraire l’existant
                </label>
              </div>
            </li>
          ))}
        </ul>

        {/* Pied : actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => onConfirm(decisions)}
            disabled={submitting}
            className="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary-600 text-sm font-medium disabled:opacity-50"
          >
            {submitting
              ? 'En cours...'
              : reextractCount > 0
                ? `Confirmer (${reextractCount} ré-extraction${reextractCount > 1 ? 's' : ''})`
                : 'Confirmer'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ReuploadDedupModal;
